// Review marks shown in one view of a repository (a pull request today):
// private to the user, stored on the server as content anchors keyed by
// repository (marks API version 3, docs/design.md "Review marks"), applied
// optimistically and queued (see marksQueue.ts).
//
// The server holds every anchor the user made on the repository, so the
// store asks only for those on the blobs its view shows at each path
// (`show`, as files are diffed). A pull request's view also holds its
// flags (review/flags.ts: threads marked reviewed, drafts set aside),
// which are not content and are kept per subject (`#<n>`, the flags
// API), and its key-based marks still pending resolution (`legacy`).
//
// What the server answered is kept per anchor with its `updated_at`, which
// only grows: a later answer replaces an entry only with a later write, so
// an answer read before a write that was acknowledged since (whose PUT
// answer is kept as it lands) never takes the write back. A full load is
// the truth for everything it asked about, so entries it no longer returns
// are dropped, unless they landed after it started.
//
// The queue holds one op per anchor (by `anchorId`), flags included:
// reviewed or not. A plan's unmarks and marks are applied in that order,
// so a merged anchor replaces the ones it absorbs.
//
// Unacknowledged writes are persisted one record per anchor
// (`<view>|<anchor ID>`, see PendingMarkRecord; the view is `owner/repo#n`
// for a pull request), so tabs on the same view merge rather than
// overwrite each other, and each tab deletes only the records it sent.
// They are restored once per store, and replayed for every view when the
// app starts (`replayPendingMarks`); a key-based write an older client left
// is replayed through the key API.
//
// A marks notice names the (path, blob) pairs a write changed; every view,
// the writing one included, looks up again those of them it shows
// (`onNotice`). No view tracks its own writes: that bookkeeping could
// swallow another tab's write to the same pair.
import { isNotInstalled } from '../api/errors';
import { FLAGS_PER_PUT, MARKS_LOOKUP_BLOBS } from '../api/limits';
import {
  getFlags,
  getLegacy,
  lookupMarks,
  putAnchors,
  putFlags,
  putMarks,
  resolveMarks,
  type MarksResolution,
  type ShownBlobs,
  type StoredAnchor,
  type StoredFlag,
  type WireAnchor,
  type WireFlag,
} from '../api/server';
import { idbDeleteIf, idbEntries, idbSet, type PendingMarkRecord } from '../api/db';
import { anchorId, fromWire, toWire, type Anchor } from '../review/anchors';
import type { Plan } from '../review/anchors';
import { FLAG_PATH, flag, isFlag } from '../review/flags';
import { MarksQueue, type PendingMarks, type PendingOp } from './marksQueue';
import { subjectKey, type Subject } from '../subject';

/**
 * The subject a review flag is stored on: its one key (`subjectKey`)
 * without `owner/repo`, which the server keeps as the repository's ID, and
 * without a ref's `//path` (a history filtered to a path is its branch's):
 * `#12`, `@feat`, `@main...ana:feat`, `:feat` (the server's flags API takes
 * this form and no other).
 */
export function flagSubject(subject: Subject): string {
  const key = subjectKey(subject.kind === 'ref' ? { ...subject, path: null } : subject);
  return key.slice(`${subject.owner}/${subject.repo}`.length);
}

/** The view's key in persisted records: `owner/repo#n`, or `owner/repo` outside a pull request. */
function viewKey(owner: string, repo: string, pull: number | null): string {
  return (pull === null ? `${owner}/${repo}` : `${owner}/${repo}#${pull}`).toLowerCase();
}

function recordKey(view: string, id: string): string {
  return `${view}|${id}`;
}

async function readPersisted(view: string): Promise<{ pending: PendingMarks; anchors: Map<string, Anchor> }> {
  const ops: PendingMarks['ops'] = {};
  const anchors = new Map<string, Anchor>();
  for (const [, r] of await idbEntries('marks', view + '|')) {
    // A key-based write from an older client: the startup replay sends it.
    if (r.pr !== view || !r.anchor) continue;
    const a = fromWire(r.anchor);
    anchors.set(anchorId(a), a);
    ops[anchorId(a)] = { reviewed: r.reviewed, seq: 0, id: r.id };
  }
  return { pending: { ops }, anchors };
}

/** Queue persistence for one view: one IndexedDB record per pending anchor. */
function persistence(view: string, anchorOf: (id: string) => Anchor | undefined) {
  const written = new Map<string, string>(); // anchor ID → op ID already stored
  return {
    async persist(pending: PendingMarks) {
      for (const [id, op] of Object.entries(pending.ops)) {
        const anchor = anchorOf(id);
        if (!op.id || !anchor || written.get(id) === op.id) continue;
        written.set(id, op.id);
        const rec: PendingMarkRecord = { pr: view, block_key: id, anchor: toWire(anchor), reviewed: op.reviewed, id: op.id };
        await idbSet('marks', recordKey(view, id), rec);
      }
    },
    async settled(ops: [string, PendingOp][]) {
      for (const [id, op] of ops) {
        if (written.get(id) === op.id) written.delete(id);
        await idbDeleteIf('marks', recordKey(view, id), (r) => r.id === op.id);
      }
    },
  };
}

/** A server time as a key that orders as the time does: `updated_at` is
 *  RFC 3339 in UTC with up to nine fractional digits, as many as needed. */
function timeKey(at: string): string {
  const m = /^([^.Z+]*)(?:\.(\d+))?(.*)$/.exec(at);
  return m ? `${m[1]}.${(m[2] ?? '').padEnd(9, '0')}${m[3]}` : at;
}

/** A flag as the flags API names it, and back: the client keeps flags as anchors on `./` paths. */
function toFlag(a: Anchor, active: boolean): WireFlag {
  return { name: a.path.slice(FLAG_PATH.length), value: a.blob, active };
}
const fromFlag = (f: StoredFlag): StoredAnchor => ({
  ...toWire(flag(FLAG_PATH + f.name, f.value)),
  reviewed: f.active,
  updated_at: f.updated_at,
});

/** Writes `anchors` (flags among them) as the APIs take them: anchors by repository, flags on `subject`. */
async function send(owner: string, repo: string, subject: string | null, anchors: (WireAnchor & { reviewed: boolean })[]): Promise<StoredAnchor[]> {
  const flags = anchors.filter((a) => a.path.startsWith(FLAG_PATH));
  const lines = anchors.filter((a) => !a.path.startsWith(FLAG_PATH));
  const out: StoredAnchor[] = [];
  if (lines.length) out.push(...(await putAnchors(owner, repo, lines)).anchors);
  // A flag needs a subject: outside a pull request a view holds none.
  if (flags.length && subject !== null) {
    for (let i = 0; i < flags.length; i += FLAGS_PER_PUT) {
      const chunk = flags.slice(i, i + FLAGS_PER_PUT).map((a) => toFlag(fromWire(a), a.reviewed));
      out.push(...(await putFlags(owner, repo, subject, chunk)).map(fromFlag));
    }
  }
  return out;
}

/** A failed lookup (or load) is tried again after this, doubling up to LOOKUP_RETRY_MAX_MS. */
export const LOOKUP_RETRY_MS = 2000;
export const LOOKUP_RETRY_MAX_MS = 60_000;

/** One anchor as the server last answered it. */
interface Entry {
  anchor: Anchor;
  reviewed: boolean;
  /** `timeKey` of its `updated_at`. */
  at: string;
  /** When it landed, in the store's own count (see `MarksStore.tick`). */
  tick: number;
}

/** `blobs` split into lookups within the server's cap. */
function lookupsFor(blobs: ReadonlyMap<string, readonly string[]>): ShownBlobs[] {
  const out: ShownBlobs[] = [];
  let chunk: ShownBlobs = {};
  let count = 0;
  for (const [path, list] of blobs) {
    for (let i = 0; i < list.length; ) {
      if (count === MARKS_LOOKUP_BLOBS) {
        out.push(chunk);
        chunk = {};
        count = 0;
      }
      const take = list.slice(i, i + MARKS_LOOKUP_BLOBS - count);
      chunk[path] = [...(chunk[path] ?? []), ...take];
      count += take.length;
      i += take.length;
    }
  }
  if (count) out.push(chunk);
  return out;
}

/** The anchors on `blobs`, in as many lookups as they take. */
async function ask(owner: string, repo: string, blobs: ReadonlyMap<string, readonly string[]>): Promise<StoredAnchor[]> {
  const answers = await Promise.all(lookupsFor(blobs).map((b) => lookupMarks(owner, repo, b)));
  return answers.flatMap((a) => a.anchors);
}

/** A marks or flags notice, as the server publishes it. */
export interface MarksNotice {
  kind: string;
  /** `marks`: the (path, blob) pairs whose anchors changed; null when a
   *  write changed more than a notice names (look up everything shown). */
  blobs?: Record<string, string[]> | null;
  /** `marks` from a pull request's key-based marks: that pull request. */
  number?: number;
  /** `flags`: the subject whose flags changed. */
  subject?: string;
}

/** Stores of mounted views, so sign-out can send their queued writes first. */
const openStores = new Set<MarksStore>();

/** Send every open store's queued writes (one attempt each). */
export async function flushOpenMarks(): Promise<void> {
  await Promise.all([...openStores].map((s) => s.flush().catch(() => {})));
}

export class MarksStore {
  /** The reviewed anchors: the server's, with the writes it has not acknowledged on top. */
  anchors: readonly Anchor[] = $state.raw([]);
  /** The private flags (review/flags.ts), as `anchors` are kept. */
  flags: readonly Anchor[] = $state.raw([]);
  /** Key-based marks the server holds as reviewed and not resolved yet (see `resolve`). */
  legacy: readonly string[] = $state.raw([]);
  loaded = $state(false);
  error: string | null = $state(null);
  /** The anchors written that the server has not acknowledged yet (shown as being saved). */
  unsent: readonly Anchor[] = $state.raw([]);
  /** The flags written that the server has not acknowledged yet. */
  unsentFlags: readonly Anchor[] = $state.raw([]);
  private puts = $state(0);
  /** A PUT of marks is in flight. */
  get sending(): boolean {
    return this.puts > 0;
  }
  private queue: MarksQueue;
  /** The server's anchors (and flags) as last answered, by ID, reviewed or not. */
  private server = new Map<string, Entry>();
  /** Counts loads started and answers kept, so a load knows what landed after it began. */
  private tick = 0;
  /** The tick of the answer whose `legacy` is shown. */
  private legacyTick = 0;
  /** Every blob shown at each path (`show`), and the pairs not looked up yet. */
  private shown = new Map<string, string[]>();
  private seen = new Set<string>();
  private fresh: ShownBlobs = {};
  private lookingUp: Promise<void> | null = null;
  /** Pairs shown whose marks are not known yet: being looked up, or a lookup failed and is tried again (`answered`). */
  private unread: ReadonlySet<string> = $state.raw(new Set());
  /** Failed lookups in a row, and the timers trying them again. */
  private lookupFailures = 0;
  /** `error` says a lookup failed (cleared once every pair is answered). */
  private lookupError = false;
  private lookupRetries = new Set<ReturnType<typeof setTimeout>>();
  /** Failed loads in a row, and the timer loading again. */
  private loadFailures = 0;
  private loadRetry: ReturnType<typeof setTimeout> | null = null;
  /** Every anchor a queued op names, by ID. */
  private known = new Map<string, Anchor>();
  private key: string;
  /** The subject flags are kept on: `#<n>` for a pull request. */
  private subject: string | null;
  private restored = false;
  private disposed = false;
  /** Every write of pending marks to IndexedDB started so far (see `persisted`). */
  private persisting: Promise<void> = Promise.resolve();

  /**
   * `pull` is the pull request the view is of, or null for a view that is
   * of none (it holds no flags or key-based marks). `onNotInstalled` is
   * called, instead of reporting an error, when the server answers 403
   * `not_installed`: it keeps no marks for the repository (see
   * `isNotInstalled`). `onSend` runs as each write of marks is sent; what
   * it returns runs once the write is answered, accepted or failed.
   */
  constructor(
    private owner: string,
    private repo: string,
    private pull: number | null,
    private onNotInstalled: () => void = () => {},
    private onSend: () => () => void = () => () => {},
  ) {
    this.key = viewKey(owner, repo, pull);
    this.subject = pull === null ? null : flagSubject({ kind: 'pull', owner, repo, number: pull });
    openStores.add(this);
    const p = persistence(this.key, (id) => this.known.get(id));
    this.queue = new MarksQueue({
      send: async (marks) => {
        const anchors = marks.flatMap((m) => {
          const a = this.known.get(m.block_key);
          return a ? [{ ...toWire(a), reviewed: m.reviewed }] : [];
        });
        if (!anchors.length) return;
        this.puts++;
        const answered = this.onSend();
        try {
          const rows = await send(owner, repo, this.subject, anchors);
          // Kept before the queue lets go of the ops, so nothing shows
          // without them in between.
          if (!this.disposed) this.keep(rows);
        } finally {
          this.puts--;
          answered();
        }
      },
      persist: (pending) => {
        const done = p.persist(pending);
        this.persisting = Promise.all([this.persisting, done.catch(() => {})]).then(() => {});
        return done;
      },
      settled: async (ops) => {
        this.recompute();
        await p.settled(ops);
      },
      onError: (e, kind) => {
        if (this.disposed) return;
        if (isNotInstalled(e)) this.onNotInstalled();
        else if (kind === 'drop') {
          this.error = `Some review marks could not be saved: ${e instanceof Error ? e.message : String(e)}`;
          void this.load();
        } else if (kind === 'park') this.error = 'Sign in again to save review marks.';
      },
    });
  }

  /**
   * The sides the view shows (a file's path and blob on each side, as it
   * is diffed): the anchors on them are looked up, once per pair, in
   * batches of what arrived while the last was in flight.
   */
  show(sides: readonly { path: string; blob: string }[]) {
    if (this.disposed) return;
    const added: string[] = [];
    for (const { path, blob } of sides) {
      const k = `${blob}:${path}`;
      if (this.seen.has(k)) continue;
      this.seen.add(k);
      added.push(k);
      const list = this.shown.get(path);
      if (list) list.push(blob);
      else this.shown.set(path, [blob]);
      (this.fresh[path] ??= []).push(blob);
    }
    if (added.length) this.unread = new Set([...this.unread, ...added]);
    if (Object.keys(this.fresh).length && !this.lookingUp) this.lookingUp = this.lookUp();
  }

  /**
   * Whether the marks on `path` at `blob` are known: shown and answered.
   * False while its lookup is under way, and after one failed, until it is
   * tried again and answered (a file there is not judged meanwhile).
   */
  answered(path: string, blob: string): boolean {
    return !this.unread.has(`${blob}:${path}`);
  }

  /** The pairs of `batch` are answered. */
  private read(keys: Iterable<string>) {
    const done = new Set(keys);
    if ([...done].some((k) => this.unread.has(k))) this.unread = new Set([...this.unread].filter((k) => !done.has(k)));
  }

  /** Look up `batch` again later, after LOOKUP_RETRY_MS doubling to LOOKUP_RETRY_MAX_MS. */
  private retryLookup(batch: ReadonlyMap<string, readonly string[]>) {
    this.lookupFailures++;
    const timer = setTimeout(
      () => {
        this.lookupRetries.delete(timer);
        if (this.disposed) return;
        for (const [path, blobs] of batch) (this.fresh[path] ??= []).push(...blobs);
        if (!this.lookingUp) this.lookingUp = this.lookUp();
      },
      Math.min(LOOKUP_RETRY_MAX_MS, LOOKUP_RETRY_MS * 2 ** (this.lookupFailures - 1)),
    );
    this.lookupRetries.add(timer);
  }

  /** Look up the pairs shown (or changed elsewhere) since the last lookup, until none is left. */
  private async lookUp() {
    // Sides shown in the same turn go together.
    await Promise.resolve();
    while (Object.keys(this.fresh).length && !this.disposed) {
      const batch = new Map(Object.entries(this.fresh));
      this.fresh = {};
      const keys = [...batch].flatMap(([path, blobs]) => blobs.map((b) => `${b}:${path}`));
      try {
        const rows = await ask(this.owner, this.repo, batch);
        if (this.disposed) break;
        this.keep(rows);
        this.read(keys);
        this.lookupFailures = 0;
        if (this.lookupError && !this.unread.size) {
          this.lookupError = false;
          this.error = null;
        }
        this.recompute();
      } catch (e) {
        if (this.disposed) break;
        // Not known: these pairs stay unread, and are asked again.
        if (isNotInstalled(e)) this.onNotInstalled();
        else {
          this.error = `Review marks could not be loaded: ${e instanceof Error ? e.message : String(e)}`;
          this.lookupError = true;
          this.retryLookup(batch);
        }
      }
    }
    this.lookingUp = null;
  }

  /**
   * A marks or flags notice of the repository, from any tab or device,
   * this one's included: looks up again the pairs it names that the view
   * shows, the flags of this view's subject, and the pending key-based
   * marks of this view's pull request. Resolves once those loads are done.
   */
  onNotice(n: MarksNotice): Promise<void> {
    if (this.disposed) return Promise.resolve();
    if (n.kind === 'flags') return n.subject === this.subject ? this.loadFlags() : Promise.resolve();
    if (n.kind !== 'marks') return Promise.resolve();
    // Past the server's bound a notice names no pair: everything shown may have changed.
    const named = n.blobs === null ? Object.fromEntries(this.shown) : (n.blobs ?? {});
    for (const [path, blobs] of Object.entries(named)) {
      const shown = this.shown.get(path);
      const mine = blobs.filter((b) => shown?.includes(b));
      if (mine.length) (this.fresh[path] ??= []).push(...mine);
    }
    if (Object.keys(this.fresh).length && !this.lookingUp) this.lookingUp = this.lookUp();
    const legacy = n.number !== undefined && n.number === this.pull ? this.loadLegacy() : Promise.resolve();
    return Promise.all([this.lookedUp(), legacy]).then(() => {});
  }

  /**
   * Fetch the server's marks for everything the view shows. A load that
   * failed is tried again after LOOKUP_RETRY_MS, doubling up to
   * LOOKUP_RETRY_MAX_MS, until one succeeds: `error` holds until then.
   */
  async load() {
    // Restore persisted writes once: later loads must not resurrect ops this
    // tab already sent (the server's `marks` notice follows every PUT).
    if (!this.restored) {
      this.restored = true;
      const { pending, anchors } = await readPersisted(this.key);
      for (const [id, a] of anchors) this.known.set(id, a);
      this.queue.restore(pending);
      this.recompute();
    }
    if (this.disposed) return;
    const start = ++this.tick;
    const asked = [...this.shown].flatMap(([path, blobs]) => blobs.map((b) => `${b}:${path}`));
    try {
      const subject = this.subject;
      const [rows, flags, legacy] = await Promise.all([
        ask(this.owner, this.repo, this.shown),
        subject === null ? Promise.resolve([]) : getFlags(this.owner, this.repo, subject),
        this.pull === null ? Promise.resolve([]) : getLegacy(this.owner, this.repo, this.pull),
      ]);
      if (this.disposed) return;
      this.replace([...rows, ...flags.map(fromFlag)], start, () => true);
      this.takeLegacy(legacy, start);
      this.read(asked);
      this.lookupError = false;
      this.error = null;
      this.loadFailures = 0;
      if (this.loadRetry) clearTimeout(this.loadRetry);
      this.loadRetry = null;
    } catch (e) {
      if (this.disposed) return;
      if (isNotInstalled(e)) this.onNotInstalled();
      else {
        this.error = `Review marks could not be loaded: ${e instanceof Error ? e.message : String(e)}`;
        this.retryLoad();
      }
    }
    this.recompute();
    this.loaded = true;
  }

  /** Load again later, after LOOKUP_RETRY_MS doubling to LOOKUP_RETRY_MAX_MS (one retry waits at a time). */
  private retryLoad() {
    this.loadFailures++;
    if (this.loadRetry) clearTimeout(this.loadRetry);
    this.loadRetry = setTimeout(
      () => {
        this.loadRetry = null;
        if (!this.disposed) void this.load();
      },
      Math.min(LOOKUP_RETRY_MAX_MS, LOOKUP_RETRY_MS * 2 ** (this.loadFailures - 1)),
    );
  }

  /** Fetch the view's flags again (another tab or device changed them). */
  private async loadFlags() {
    if (this.subject === null) return;
    const start = ++this.tick;
    try {
      const flags = await getFlags(this.owner, this.repo, this.subject);
      if (this.disposed) return;
      this.replace(flags.map(fromFlag), start, (e) => isFlag(e.anchor));
      this.recompute();
    } catch {
      // The next full load reads them again.
    }
  }

  /** Fetch the pull request's pending key-based marks again. */
  private async loadLegacy() {
    if (this.pull === null) return;
    const start = ++this.tick;
    try {
      const legacy = await getLegacy(this.owner, this.repo, this.pull);
      if (!this.disposed) this.takeLegacy(legacy, start);
    } catch {
      // The next full load reads them again.
    }
  }

  private takeLegacy(legacy: readonly { block_key: string }[], tick: number) {
    if (tick <= this.legacyTick) return;
    this.legacyTick = tick;
    this.legacy = legacy.map((l) => l.block_key);
  }

  private entry(w: StoredAnchor, tick: number): [string, Entry] {
    const anchor = fromWire(w);
    return [anchorId(anchor), { anchor, reviewed: w.reviewed, at: timeKey(w.updated_at), tick }];
  }

  /** Keep what an answer says of each anchor it names, where it was written later than what is kept. */
  private keep(rows: readonly StoredAnchor[]) {
    const tick = ++this.tick;
    for (const w of rows) {
      const [id, e] = this.entry(w, tick);
      const had = this.server.get(id);
      if (!had || had.at <= e.at) this.server.set(id, e);
    }
  }

  /**
   * Take a load's answer, started at `start`, as what the server holds of
   * the entries `asked` selects, keeping what landed since.
   */
  private replace(rows: readonly StoredAnchor[], start: number, asked: (e: Entry) => boolean) {
    const next = new Map(rows.map((w) => this.entry(w, start)));
    for (const [id, had] of this.server) {
      if (had.tick <= start && asked(had)) continue;
      const got = next.get(id);
      if (!got || got.at <= had.at) next.set(id, had);
    }
    this.server = next;
  }

  /** `anchors`, `flags` and the unsent ones from the server's marks and the queue. */
  private recompute() {
    const on = new Map<string, Anchor>();
    for (const [id, s] of this.server) if (s.reviewed) on.set(id, s.anchor);
    const pending = this.queue.pending();
    for (const [id, reviewed] of pending) {
      const a = this.known.get(id);
      if (!a) continue;
      if (reviewed) on.set(id, a);
      else on.delete(id);
    }
    const all = [...on.values()];
    this.anchors = all.filter((a) => !isFlag(a));
    this.flags = all.filter(isFlag);
    const unsent = [...pending.keys()].flatMap((id) => {
      const a = this.known.get(id);
      return a ? [a] : [];
    });
    this.unsent = unsent.filter((a) => !isFlag(a));
    this.unsentFlags = unsent.filter(isFlag);
  }

  /**
   * Remove the anchors in `plan.unset`, then store those in `plan.set` (a
   * plan from `review/model` `planLines` or `planFile`).
   *
   * Not atomic on the server: the queue coalesces by anchor (a later
   * write to an anchor replaces the one waiting, whichever plan it came
   * from) and sends MARKS_PER_PUT anchors per PUT, so a plan can straddle
   * two PUTs. A plan is a few anchors against thousands per PUT, so that
   * takes a backlog of thousands queued (e.g. offline). If one of the two
   * PUTs is then dropped (a 4xx, such as a cap), the marks are loaded
   * again, so the screen shows what the server holds, and the error says
   * so.
   */
  apply(plan: Plan) {
    // Stopped (`forget`, `dispose`): the server keeps nothing more from this
    // store, so nothing is shown or persisted to be replayed later.
    if (this.disposed || (!plan.set.length && !plan.unset.length)) return;
    for (const [list, reviewed] of [
      [plan.unset, false],
      [plan.set, true],
    ] as const) {
      for (const a of list) {
        const id = anchorId(a);
        this.known.set(id, { ...a });
        this.queue.set(id, reviewed);
      }
    }
    this.recompute();
  }

  /**
   * Record what this user's key-based marks on the pull request cover (a
   * client resolves them once, see docs/design.md "Review marks"), and
   * show the server's answer. The server applies a key's resolution only
   * while it is pending, so a repeat, or another device's, changes
   * nothing.
   */
  async resolve(body: MarksResolution): Promise<void> {
    if (this.disposed || this.pull === null || (!body.resolved.length && !body.unresolvable.length)) return;
    // A write of marks like any other: said as it is sent and answered.
    const answered = this.onSend();
    let v: Awaited<ReturnType<typeof resolveMarks>>;
    try {
      v = await resolveMarks(this.owner, this.repo, this.pull, body);
    } finally {
      answered();
    }
    if (this.disposed) return;
    this.keep(v.anchors);
    this.takeLegacy(v.legacy, this.tick);
    this.recompute();
  }

  /**
   * Resolves once every side shown so far has been looked up, answered or
   * failed: the diff model holds a file's state unknown until then.
   */
  lookedUp(): Promise<void> {
    return this.lookingUp ?? Promise.resolve();
  }

  get pendingWrites() {
    return this.queue.size;
  }

  flush() {
    return this.queue.flush();
  }

  /**
   * Resolves once every write of pending marks to IndexedDB started so far
   * has finished, stored or failed (each change is persisted as it is
   * made, apart from its PUT). For tests.
   */
  persisted(): Promise<void> {
    return this.persisting;
  }

  /** Stop (as `dispose`) and drop the marks shown: the server keeps none for this repository any more. */
  forget() {
    if (!this.disposed) this.dispose();
    this.server.clear();
    this.anchors = [];
    this.flags = [];
    this.legacy = [];
    this.error = null;
  }

  /** Make one last attempt to send queued writes, then stop (see MarksQueue.dispose). */
  dispose() {
    this.disposed = true;
    for (const t of this.lookupRetries) clearTimeout(t);
    this.lookupRetries.clear();
    if (this.loadRetry) clearTimeout(this.loadRetry);
    this.loadRetry = null;
    openStores.delete(this);
    void this.queue.flush();
    this.queue.dispose();
  }
}

/**
 * Send every persisted, unacknowledged mark write for every view (run once
 * the user is known), so marks made in a tab that closed before its PUT
 * landed reach the server even if that view is never opened again.
 * Anchors go to the marks API and a pull request's flags to the flags
 * API; a key-based write left by an older client goes to the key API,
 * which keeps it pending for resolution.
 */
export async function replayPendingMarks(): Promise<void> {
  const byView = new Map<string, PendingMarkRecord[]>();
  for (const [, r] of await idbEntries('marks')) {
    const list = byView.get(r.pr) ?? [];
    list.push(r);
    byView.set(r.pr, list);
  }
  await Promise.all(
    [...byView].map(async ([view, records]) => {
      const m = /^([^/]+)\/([^#]+)(?:#(\d+))?$/.exec(view);
      if (!m) return;
      const [, owner, repo, n] = m;
      const pull = n === undefined ? null : Number(n);
      const wire = new Map<string, WireAnchor>(records.flatMap((r) => (r.anchor ? [[r.block_key, r.anchor] as const] : [])));
      const p = persistence(view, (id) => {
        const w = wire.get(id);
        return w && fromWire(w);
      });
      const q = new MarksQueue({
        send: async (marks) => {
          const anchors = marks.flatMap((x) => {
            const w = wire.get(x.block_key);
            return w ? [{ ...w, reviewed: x.reviewed }] : [];
          });
          const keys = marks.filter((x) => !wire.has(x.block_key));
          if (anchors.length) await send(owner, repo, pull === null ? null : `#${pull}`, anchors);
          if (keys.length && pull !== null) await putMarks(owner, repo, pull, keys);
        },
        persist: () => {},
        settled: p.settled,
      });
      q.restore({ ops: Object.fromEntries(records.map((r) => [r.block_key, { reviewed: r.reviewed, seq: 0, id: r.id }])) });
      await q.flush();
      q.dispose();
    }),
  );
}
